import { useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import clsx from 'clsx'
import { format, parseISO } from 'date-fns'
import { Row, Table } from '@tanstack/react-table'

import { BulkCurtailment, Log, RouteSelectionMode } from '@/types/logs'
import { InputFormValuesEdit } from '@/modules/CombinedLogs/types'
import { Input } from '@/ui/Input'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'

import styles from '../../CombinedLogs.module.scss'

import { TableFormRouteInput } from './FormInputs/TableFormRouteInput'
import { TableFormDelayInput } from './FormInputs/TableFormDelayInput'
import { TableFormCodeInput } from './FormInputs/TableFormCodeInput'
import { TableFormLocationInput } from './FormInputs/TableFormLocationInput'
import { TableFormTripInput } from './FormInputs/TableFormTripInput'
import { TableFormDescriptionInputs } from './FormInputs/TableFormDescriptionInput'
import { TableFormLogActionInput } from './FormInputs/TableFormLogActionInput'
import { TableFormOriginalTripInput } from './FormInputs/TableFormOriginalTripInput'
import { TableFormDutyInput } from './FormInputs/TableFormDutyInput'
import { Role } from '@/types/auth'
import { useAuthStore } from '@/store/auth-store'
import { checkBulkCurtailmentValues } from '@/modules/CombinedLogs/helpers/checkBulkCurtailmentValues'
import { TableFormTimeInput } from './FormInputs/TimeInput/TableFormTimeInput'
import { Unfinished } from '../information/Unfinished'
import { Lock } from '../Lock'
import { useManageCombinedLogsColumns } from '@/modules/CombinedLogs/hooks/useManageCombinedLogsColumns'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { getNameInitials } from '@/helpers/getNameInitials'
import { Actions } from '@/modules/CombinedLogs/ui/components/Actions'

interface Props {
    form: UseFormReturn<InputFormValuesEdit>
    isActive: boolean
    table: Table<Log>
    row: Row<Log>
    ref: React.RefObject<HTMLTableRowElement>
    isUrgent: boolean
}

export const TableFormEdit = ({ form, isActive, table, row, ref, isUrgent }: Props) => {
    const { role } = useAuthStore()
    const isExtraMileage = form.watch('code') === 'E'
    const eventLogId = form.watch('eventLogId')
    const isLinked = !!eventLogId && table.getRowModel().rows[row.index - 1]?.original.id === eventLogId
    const columnVisibility = useManageCombinedLogsColumns()
    const hasPermission = useCheckPermissions()
    const {
        setRoute,
        setIsBulkCurtailmentLog,
        setReport,
        setLocationType,
        setMechanicalInformation,
        isBulkCurtailment,
    } = useCombinedLogsTableFormStateStore()
    const log = row.original

    const bulkCurtailmentTripTo = form.watch('bulkCurtailmentTripTo')
    const bulkCurtailmentRunTo = form.watch('bulkCurtailmentRunTo')
    const bulkCurtailmentDutyTo = form.watch('bulkCurtailmentDutyTo')
    const bulkCurtailmentFleetTo = form.watch('bulkCurtailmentFleetTo')

    const isZeroMileage = form.watch('isZeroMileage')

    const bulkCurtailment = useMemo<Partial<BulkCurtailment>>(
        () => ({
            tripTo: bulkCurtailmentTripTo,
            runTo: bulkCurtailmentRunTo,
            dutyTo: bulkCurtailmentDutyTo,
            fleetTo: bulkCurtailmentFleetTo,
        }),
        [bulkCurtailmentTripTo, bulkCurtailmentRunTo, bulkCurtailmentDutyTo, bulkCurtailmentFleetTo]
    )

    const isBulkCurtailmentValid = checkBulkCurtailmentValues({ bulkCurtailment } as unknown as Log)

    useEffect(() => {
        setRoute(log.routes[0])

        const baseFormData = {
            code: log.code,
            reference: log.reference,
            routes:
                log.routes && log.routeSelectionMode !== RouteSelectionMode.None
                    ? log.routeSelectionMode === RouteSelectionMode.All
                        ? 'N/A'
                        : log.routes.join(', ')
                    : '',
            routeSelectionMode: log.routeSelectionMode,
            trip: log.trip,
            rtnTrips: log.rtnTrips ? log.rtnTrips.map((trip) => trip.trip).join(',') : '',
            run: log.run,
            duty: log.duty ? log.duty.join(',') : '',
            fleet: log.fleet ? log.fleet.join(',') : '',
            locationFrom: log.locationFrom,
            locationTo: log.locationTo,
            direction: log.direction,
            description: log.descriptionInfo.description,
            extraInfo: log.descriptionInfo.extraInfo,
            strategy: log.descriptionInfo.strategy,
            delay: log.delayInMinutes ? log.delayInMinutes.toString() : '',
            isZeroMileage: log.isZeroMileage ? 'true' : 'false',
            mileage: log.isZeroMileage ? '0.00' : log.mileage ? log.mileage.toString() : '',
            author: log.author,
            timeStamp: format(parseISO(log.timestamp), 'HH:mm'),
            selectedCodeTypeReasonId: log.codeTypeReasonInfo?.reasonId,
            eventLogId: log.eventLog?.id,
        }

        if (log.originalTrip) {
            form.reset({
                ...baseFormData,
                originalRoutes: log.originalTrip.routes ? log.originalTrip.routes.join(',') : '',
                originalTrip: log.originalTrip.trip,
                originalRtnTrips: log.originalTrip.rtnTrips
                    ? log.originalTrip.rtnTrips.map((trip) => trip.trip).join(',')
                    : '',
                originalRun: log.originalTrip.run,
                originalDuty: log.originalTrip.duty ? log.originalTrip.duty.join(',') : '',
                originalFleet: log.originalTrip.fleet ? log.originalTrip.fleet.join(',') : '',
            })
        } else {
            form.reset({
                ...baseFormData,
                bulkCurtailmentTripTo: log.bulkCurtailment?.tripTo,
                bulkCurtailmentDutyTo: log.bulkCurtailment?.dutyTo,
                bulkCurtailmentFleetTo: log.bulkCurtailment?.fleetTo,
                bulkCurtailmentRunTo: log.bulkCurtailment?.runTo,
            })
        }

        setIsBulkCurtailmentLog(!!log.bulkCurtailment)
        setReport(log.report)
        setMechanicalInformation(log.mechanicalInfo)
        setLocationType(log.locationType)
    }, [])

    useEffect(() => {
        setReport(log.report)
        setMechanicalInformation(log.mechanicalInfo)
    }, [log])

    return (
        <>
            {table.getHeaderGroups().map((headerGroup) => (
                <tr
                    ref={ref}
                    className={clsx(styles.formRow, styles.active, {
                        [styles.hidden]: !isActive,
                        [styles.urgentRow]: isUrgent,
                    })}
                    data-urgent={isUrgent}
                    key={headerGroup.id}
                >
                    <td
                        className={clsx(styles.formCell, styles.alignCenterFormCell, {
                            [styles.linkElement]: isLinked,
                        })}
                        key={'code'}
                    >
                        <TableFormCodeInput name="code" form={form} isLinked={isLinked} />
                    </td>
                    <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'reference'}>
                        <span className={styles.reference}>{row.original.reference}</span>
                        {isExtraMileage && (
                            <div>
                                <p className={styles.originalTripFormTitle}>Original trip:</p>
                            </div>
                        )}
                    </td>
                    <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'routes'}>
                        {isBulkCurtailment && isLinked && row.original.routes.length === 1 ? (
                            <span className={styles.reference}>{row.original.routes[0]}</span>
                        ) : (
                            <>
                                <TableFormRouteInput name="routes" control={form.control} form={form} />
                                {isExtraMileage && (
                                    <div className={styles.originalRoutesFormContainer}>
                                        <TableFormRouteInput name="originalRoutes" control={form.control} form={form} />
                                    </div>
                                )}
                            </>
                        )}
                    </td>
                    <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'trip'}>
                        <TableFormTripInput form={form} />
                        {isExtraMileage && (
                            <div className={styles.originalRoutesFormContainer}>
                                <TableFormOriginalTripInput form={form} />
                            </div>
                        )}
                        {isBulkCurtailmentValid && (
                            <div className={styles.formCell}>
                                <span>to</span>
                            </div>
                        )}
                    </td>
                    <td className={styles.formCell} key={'run'}>
                        <Input
                            {...form.register('run')}
                            variant="table"
                            type="text"
                            transparent
                            fullWidth
                            placeholder={'Run'}
                        />
                        {isExtraMileage && (
                            <div className={styles.originalRoutesFormContainer}>
                                <Input
                                    {...form.register('originalRun')}
                                    variant="table"
                                    type="text"
                                    transparent
                                    fullWidth
                                    placeholder={'Run'}
                                />
                            </div>
                        )}
                        {isBulkCurtailmentValid && bulkCurtailment.runTo && (
                            <div className={clsx(styles.formCell, styles.bulkCurtailmentValue)}>
                                <span>{bulkCurtailment.runTo}</span>
                            </div>
                        )}
                    </td>
                    <td className={styles.formCell} key={'duty'}>
                        <TableFormDutyInput form={form} name="duty" />
                        {isExtraMileage && (
                            <div className={styles.originalRoutesFormContainer}>
                                <TableFormDutyInput form={form} name="originalDuty" />
                            </div>
                        )}
                        {isBulkCurtailmentValid && bulkCurtailment.dutyTo && (
                            <div className={clsx(styles.formCell, styles.bulkCurtailmentValue)}>
                                <span>{bulkCurtailment.dutyTo}</span>
                            </div>
                        )}
                    </td>
                    <td className={styles.formCell} key={'fleet'}>
                        <Input
                            {...form.register('fleet')}
                            variant="table"
                            type="text"
                            transparent
                            fullWidth
                            placeholder={'Fleet'}
                        />
                        {isExtraMileage && (
                            <div className={styles.originalRoutesFormContainer}>
                                <Input
                                    {...form.register('originalFleet')}
                                    variant="table"
                                    type="text"
                                    transparent
                                    fullWidth
                                    placeholder={'Fleet'}
                                />
                            </div>
                        )}
                        {isBulkCurtailmentValid && bulkCurtailment.fleetTo && (
                            <div className={clsx(styles.formCell, styles.bulkCurtailmentValue)}>
                                <span>{bulkCurtailment.fleetTo}</span>
                            </div>
                        )}
                    </td>
                    <td className={styles.formCell} style={{ textAlign: 'start' }} key={'locationFrom'}>
                        <TableFormLocationInput isLinked={isLinked} form={form} />
                    </td>
                    <td className={styles.formCell} style={{ textAlign: 'start' }} key={'description'}>
                        <TableFormDescriptionInputs
                            form={form}
                            table={table}
                            row={row}
                            isBulkCurtailment={isBulkCurtailment}
                        />
                    </td>
                    {role !== Role.MileageClerk && (
                        <td className={styles.formCell} key={'delay'}>
                            <TableFormDelayInput name="delay" control={form.control} />
                        </td>
                    )}
                    <td className={styles.formCell} key={'mileage'}>
                        <Input
                            {...(isZeroMileage === 'true' ? { value: '0.00' } : form.register('mileage'))}
                            variant="table"
                            type="number"
                            transparent
                            readOnly
                            fullWidth
                            className={styles.greyInput}
                            placeholder={'Mileage'}
                        />
                    </td>
                    <td className={styles.formCell} key={'author'}>
                        <Input
                            variant="table"
                            type="text"
                            transparent
                            readOnly
                            fullWidth
                            className={styles.greyInput}
                            placeholder={'Author'}
                            value={row.original.originalAuthor ? getNameInitials(row.original.originalAuthor) : '-'}
                        />
                    </td>
                    <td className={styles.formCell} key={'timeStamp'}>
                        <TableFormTimeInput form={form} />
                    </td>
                    {role === Role.Controller && (
                        <td className={styles.formCell} key={'action'}>
                            <TableFormLogActionInput
                                form={form}
                                isRowSelected={row.getIsSelected()}
                                selectedRow={row}
                            />
                        </td>
                    )}
                    {columnVisibility[COMBINED_LOGS_COLUMNS.Unfinished] && hasPermission('log', 'manageUnfinished') && (
                        <td className={styles.formCell} key={'unfinished'}>
                            <Unfinished log={row.original} />
                        </td>
                    )}
                    {columnVisibility[COMBINED_LOGS_COLUMNS.Lock] && (
                        <td className={styles.formCell} key={'lock'}>
                            <Lock id={row.original.id} log={row.original} />
                        </td>
                    )}
                    <td className={styles.formCell} key={'actionMenu'}>
                        <Actions row={row} />
                    </td>
                    <td className={styles.formCell} key={'menu'}></td>
                </tr>
            ))}
        </>
    )
}
